import { OpenPanel } from "@openpanel/web";
import { getPublicEnv } from "./public-env";

const env = getPublicEnv();

// Module-level singleton, mirroring OpenPanel's own TanStack Start app (apps/start/src/utils/op.ts).
// SSR-safe: the SDK skips all browser wiring on the server, and `disabled` keeps events from being
// sent outside production. The client id and API URL are public (shipped to every browser), so
// they're hardcoded rather than plumbed through env.
export const op = new OpenPanel({
  clientId: "1f04b25a-d424-4369-b64c-476ee90ab7b6",
  // Same-origin proxy (routes/api/op/track) so adblockers don't drop events.
  apiUrl: "/api/op",
  disabled: !env.OPENPANEL_ENABLED,
  // OpenPanel takes a session's OS and device from its first event, so router.tsx starts tracking
  // once the device context is set.
  trackScreenViews: false,
  trackOutgoingLinks: true,
  trackAttributes: true,
});

// `undefined` values are dropped on serialization, so unset env vars never reach OpenPanel.
if (env.APP_VERSION || env.ASPNETCORE_ENVIRONMENT) {
  op.setGlobalProperties({
    app_version: env.APP_VERSION || undefined,
    environment: env.ASPNETCORE_ENVIRONMENT || undefined,
  });
}

let identifiedProfileId: string | null = null;
let identifiedSchoolId: string | null = null;
let flaggedImpersonation = false;

interface OpenPanelUser {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

interface OpenPanelSchool {
  id: string;
  name: string;
  brin: string;
  licentieType: string;
}

/**
 * Identify the signed-in user (profileId = email) and tag the session with the active school as an
 * OpenPanel group (B2B analytics per school), or clear both on logout. Deduped so the
 * per-navigation router subscription doesn't repeat identify/group calls.
 *
 * Impersonated sessions identify as the impersonated user, with `impersonated: true` stamped on
 * every event so support/admin activity can be filtered out of reports.
 */
export function syncOpenPanelUser(
  user: OpenPanelUser | null,
  school: OpenPanelSchool | null,
  options?: { impersonated?: boolean },
): void {
  const profileId = user?.email ?? null;
  const impersonated = options?.impersonated === true;

  if (impersonated !== flaggedImpersonation) {
    // `undefined` overwrites the key after the merge and is dropped on serialization, so regular
    // sessions don't carry `impersonated: false` on every event.
    op.setGlobalProperties({ impersonated: impersonated || undefined });
    flaggedImpersonation = impersonated;
  }

  if (profileId !== identifiedProfileId) {
    if (profileId) {
      void op.identify({
        profileId,
        email: profileId,
        firstName: user?.firstName || undefined,
        lastName: user?.lastName || undefined,
      });
    } else {
      // Resets profileId, device/session ids AND the groups array — logout drops the school group.
      op.clear();
      identifiedSchoolId = null;
    }
    identifiedProfileId = profileId;
  }

  // Outside a school route (school select, admin) the last school sticks until switch or logout.
  if (!profileId || !school || school.id === identifiedSchoolId) return;

  // setGroup only ever appends to op.groups; reset first so a school switch replaces the group.
  op.groups = [];
  void op.setGroup(school.id);
  void op.upsertGroup({
    id: school.id,
    type: "school",
    name: school.name,
    properties: { brin: school.brin, licentieType: school.licentieType },
  });
  identifiedSchoolId = school.id;
}

/** Increment a numeric property on the identified profile (visible on the OpenPanel profile page). */
export function incrementProfileProperty(property: string): void {
  if (!identifiedProfileId) return;
  void op.increment({ profileId: identifiedProfileId, property });
}
